import { useAtomValue } from "@effect-atom/atom-react";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { clawInstallModeAtom } from "~/lib/atoms/clawinstall-theme.ts";

/**
 * ClawInstall layout route
 *
 * Wraps all ClawInstall-specific routes. Domain routing is handled
 * by TanStack Router's URL rewrite system in router.tsx.
 *
 * The `data-mode` attribute mirrors the light/dark atom and drives the dark
 * token block in globals.css (overriding the OS `prefers-color-scheme`
 * default). `suppressHydrationWarning` covers the SSR(light) → client(persisted)
 * attribute swap.
 *
 * @since 1.0.0
 */
export const Route = createFileRoute("/clawinstall")({
  component: ClawInstallLayout,
});

/**
 * Parser-blocking inline script that stamps the persisted (or OS-preferred)
 * mode onto the layout div BEFORE first paint. SSR has no localStorage, so the
 * server always emits the default mode and hydration corrects it a beat later
 * — that beat is a visible light-mode flash for dark-mode customers on every
 * full page load. The script runs as the parser reaches it (its parent — the
 * layout div — already exists; nothing has painted yet), so the correct
 * tokens apply from the first frame. Resolution mirrors
 * `clawinstall-theme.ts`: stored choice, else `prefers-color-scheme`, else
 * light. React does not re-execute inline scripts on hydration, and the
 * div's `suppressHydrationWarning` covers the attribute mismatch.
 *
 * @since 1.0.0
 */
export const PREPAINT_MODE_SCRIPT = `(function () {
  try {
    var stored = window.localStorage && window.localStorage.getItem("clawinstall-theme");
    var mode = stored === "dark" || stored === "light"
      ? stored
      : (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");
    document.currentScript.parentElement.setAttribute("data-mode", mode);
  } catch (_) {}
})();`;

function ClawInstallLayout() {
  const mode = useAtomValue(clawInstallModeAtom);
  return (
    <div
      data-mode={mode}
      suppressHydrationWarning
      className="theme-clawinstall flex min-h-screen flex-col bg-surface text-on-surface"
    >
      <script dangerouslySetInnerHTML={{ __html: PREPAINT_MODE_SCRIPT }} />
      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  );
}
